import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Country, CountryService } from 'ng-phone-number';

export const PHONE_COUNTRY_MISMATCH_MESSAGE =
  'The phone number country code must match the selected worker country. Please update the phone number and try again.';

export const PHONE_DUPLICATE_MESSAGE =
  'This phone number is already associated with an existing worker profile. Please enter a unique phone number or verify the existing worker record.';

// Strips spacing/formatting punctuation for duplicate comparison, keeping digits
// and a leading '+'. Does not infer a default country code for bare local numbers.
export function normalizePhoneNumber(phone: string): string {
  return (phone || '').replace(/[\s\-.()]/g, '');
}

// Safety net for Salesforce Country__c/Name values that don't literally match
// ng-phone-number's built-in title-cased English country names.
const COUNTRY_NAME_ALIASES: { [normalizedAlias: string]: string } = {
  'usa': 'united states',
  'us': 'united states',
  'united states of america': 'united states',
  'united states of america usa': 'united states',
  'uk': 'united kingdom',
  'great britain': 'united kingdom',
  'britain': 'united kingdom',
  'south korea': 'korea',
  'korea, republic of': 'korea',
  'russia': 'russian federation',
  'ivory coast': 'cote divoire',
  'uae': 'united arab emirates',
  'vietnam': 'viet nam'
};

@Injectable({ providedIn: 'root' })
export class PhoneCountryService {

  private readonly countryService = new CountryService();

  private normalize(name: string): string {
    return (name || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[^a-z0-9 ]/g, '');
  }

  resolveCountry(addressCountryName: string): Country | null {
    if (!addressCountryName) { return null; }
    const normalized = this.normalize(addressCountryName);
    const target = COUNTRY_NAME_ALIASES[normalized] || normalized;
    const all = this.countryService.getCountries() || [];
    console.log('resolveCountry>>>', addressCountryName, normalized, target, all);
    return all.find(c => this.normalize(c.name) === target) || null;
  }

  getIsoCode(addressCountryName: string): string | null {
    const match = this.resolveCountry(addressCountryName);
    return match ? match.countryCode : null;
  }

  getDialCode(addressCountryName: string): string | null {
    const match = this.resolveCountry(addressCountryName);
    return match ? `+${match.dialCode}` : null;
  }

  // NOTE: despite ng-phone-number's .d.ts declaring `allowedCountries: Country[]`,
  // PhoneNumberComponent#ngOnInit actually passes it straight into
  // CountryService#getCountriesByISO(listOfIso), which calls `iso.toLowerCase()`
  // on each element — i.e. at runtime it must be an array of ISO-2 code strings,
  // not Country objects.
  getAllowedCountries(addressCountryName: string): string[] | null {
    const iso = this.getIsoCode(addressCountryName);
    return iso ? [iso] : null;
  }

  isMismatch(addressCountryName: string, currentDialCode: string): boolean {
    const expected = this.getDialCode(addressCountryName);
    if (!expected || !currentDialCode) { return true; }
    return expected !== currentDialCode;
  }
}

export function phoneCountryMatchValidator(
  countryControlName: string,
  phoneControlNames: string[],
  service: PhoneCountryService
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const countryValue = group.get(countryControlName) ? group.get(countryControlName).value : null;

    phoneControlNames.forEach(name => {
      const phoneCtrl = group.get(name);
      if (!phoneCtrl) { return; }

      const existingErrors: ValidationErrors = phoneCtrl.errors || {};
      const hadMismatch = !!existingErrors.countryCodeMismatch;
      const otherErrors: ValidationErrors = {};
      Object.keys(existingErrors).forEach(key => {
        if (key !== 'countryCodeMismatch') { otherErrors[key] = existingErrors[key]; }
      });
      const hasOtherErrors = Object.keys(otherErrors).length > 0;

      if (phoneCtrl.value && countryValue) {
        const expectedDialCode = service.getDialCode(countryValue);
        const actualValue = (phoneCtrl.value + '').replace(/\s+/g, '');
        const mismatch = !!expectedDialCode && !actualValue.startsWith(expectedDialCode);
        if (mismatch) {
          otherErrors.countryCodeMismatch = true;
          phoneCtrl.setErrors(otherErrors);
        } else if (hadMismatch) {
          phoneCtrl.setErrors(hasOtherErrors ? otherErrors : null);
        }
      } else if (hadMismatch) {
        phoneCtrl.setErrors(hasOtherErrors ? otherErrors : null);
      }
    });

    return null;
  };
}
